import React, { useContext, useMemo, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Table, Input, Select } from "lib/ui";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
import {
  ShoppingBag,
  Wallet,
  Users as UsersIcon,
  Search,
  TrendingUp,
  AlertCircle,
  Plus,
  ArrowRight,
  Sparkles,
  Package,
} from "lucide-react";
import { convertToReadableDate } from "../Data/Data";
import { AuthContext } from "../../context/Context";
import "./Dashboard.css";

const { Option } = Select;

const CHART_COLORS = [
  "#5b21b6",
  "#7c3aed",
  "#a78bfa",
  "#38bdf8",
  "#34d399",
  "#fbbf24",
  "#f472b6",
];

function orderTimestamp(o) {
  const raw = o?.createdAt;
  if (raw == null) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function formatAED(value) {
  const n = Number(value) || 0;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function statusTone(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("cancel") || s.includes("reject")) {
    return "bg-red-50 text-red-700 ring-red-200/80";
  }
  if (
    s.includes("complete") ||
    s.includes("deliver") ||
    s.includes("done") ||
    s.includes("paid")
  ) {
    return "bg-emerald-50 text-emerald-800 ring-emerald-200/80";
  }
  if (s.includes("pending") || s.includes("wait") || s.includes("process")) {
    return "bg-amber-50 text-amber-800 ring-amber-200/80";
  }
  if (s.includes("confirm") || s.includes("active") || s.includes("pick")) {
    return "bg-violet-50 text-violet-800 ring-violet-200/80";
  }
  return "bg-slate-100 text-slate-700 ring-slate-200/80";
}

function StatusBadge({ status }) {
  const label = (status || "Unknown").trim() || "Unknown";
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusTone(label)}`}
    >
      {label}
    </span>
  );
}

function KpiCard({ label, value, hint, icon: Icon, accent }) {
  const accents = {
    violet:
      "from-violet-600/10 via-violet-500/5 to-transparent text-violet-600",
    sky: "from-sky-500/10 via-sky-400/5 to-transparent text-sky-600",
    emerald:
      "from-emerald-500/10 via-emerald-400/5 to-transparent text-emerald-600",
    amber:
      "from-amber-500/10 via-amber-400/5 to-transparent text-amber-600",
  };
  const iconBg = {
    violet: "bg-violet-600 text-white shadow-lg shadow-violet-500/30",
    sky: "bg-sky-500 text-white shadow-lg shadow-sky-500/25",
    emerald: "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25",
    amber: "bg-amber-500 text-white shadow-lg shadow-amber-500/25",
  };

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-5 shadow-card transition hover:-translate-y-0.5 hover:shadow-card-hover`}
    >
      <div
        className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accents[accent]}`}
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {label}
          </p>
          <p className="mt-2 text-2xl font-extrabold tabular-nums tracking-tight text-slate-900 md:text-[1.65rem]">
            {value}
          </p>
          {hint ? (
            <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconBg[accent]}`}
        >
          <Icon size={20} strokeWidth={2.25} aria-hidden />
        </div>
      </div>
    </article>
  );
}

function Panel({ title, subtitle, action, children, className = "" }) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-card ${className}`}
    >
      <header className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

const Dashboard = () => {
  const [searchName, setSearchName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(undefined);
  const { Users, order } = useContext(AuthContext);

  const liveOrders = useMemo(() => {
    const list = Array.isArray(order) ? order : [];
    return [...list]
      .filter((item) => item?.position !== "driver")
      .sort((a, b) => {
        const tb = orderTimestamp(b) ?? 0;
        const ta = orderTimestamp(a) ?? 0;
        return tb - ta;
      });
  }, [order]);

  const getName = useCallback(
    (userRef) => {
      const id =
        typeof userRef === "object" && userRef !== null
          ? userRef._id ?? userRef
          : userRef;
      const user = Users?.find((u) => u._id === id);
      return user?.name ?? "Unknown";
    },
    [Users]
  );

  const filteredOrders = useMemo(() => {
    const q = searchName.trim().toLowerCase();
    const statusLow = selectedStatus
      ? String(selectedStatus).trim().toLowerCase()
      : "";
    return liveOrders.filter((item) => {
      const name = getName(item.userId).toLowerCase();
      const okName = !q || name.includes(q);
      const st = (item.status || "").toLowerCase();
      const okStatus = !statusLow || st === statusLow;
      return okName && okStatus;
    });
  }, [liveOrders, searchName, selectedStatus, getName]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    liveOrders.forEach((o) => {
      const s = (o.status || "").trim();
      if (s) set.add(s);
    });
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [liveOrders]);

  const totalAmount = useMemo(
    () =>
      filteredOrders.reduce(
        (sum, item) => sum + Number(item.totalPrice ?? 0),
        0
      ),
    [filteredOrders]
  );

  const unpaidCount = useMemo(
    () => filteredOrders.filter((o) => Number(o.balance) > 0).length,
    [filteredOrders]
  );

  const avgOrderValue = useMemo(() => {
    if (!filteredOrders.length) return 0;
    return totalAmount / filteredOrders.length;
  }, [filteredOrders, totalAmount]);

  const chartData = useMemo(() => {
    const byDay = new Map();
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    filteredOrders.forEach((o) => {
      const t = orderTimestamp(o);
      if (t == null || t < cutoff) return;
      const day = new Date(t).toISOString().slice(0, 10);
      byDay.set(day, (byDay.get(day) || 0) + Number(o.totalPrice || 0));
    });
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([iso, total]) => ({
        date: new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        }),
        total,
      }));
  }, [filteredOrders]);

  const statusData = useMemo(() => {
    const counts = new Map();
    filteredOrders.forEach((o) => {
      const raw = (o.status || "").trim() || "Unknown";
      const label =
        raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredOrders]);

  const hasFilters = Boolean(searchName.trim() || selectedStatus);
  const ordersCount = liveOrders.length;
  const usersCount = Users?.length ?? 0;
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const clearFilters = () => {
    setSearchName("");
    setSelectedStatus(undefined);
  };

  return (
    <div className="overview-page space-y-6 pb-10 md:space-y-8">
      {/* Hero header */}
      <header className="overview-hero relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-brand-deeper via-brand-dark to-brand px-6 py-7 text-white shadow-[0_20px_50px_-12px_rgba(91,33,182,0.45)] md:px-8 md:py-8">
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-violet-400/20 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-violet-100 ring-1 ring-white/15">
              <Sparkles size={14} aria-hidden />
              Operations overview
            </p>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight md:text-3xl">
              Welcome back
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-violet-100/90">
              {todayLabel} · Track revenue, order flow, and customer activity in
              real time. Filters update every metric on this page instantly.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <Link
              to="/neworder"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-brand-dark shadow-lg transition hover:bg-violet-50"
            >
              <Plus size={18} strokeWidth={2.5} aria-hidden />
              New order
            </Link>
            <Link
              to="/orders"
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition hover:bg-white/15"
            >
              All orders
              <ArrowRight size={16} aria-hidden />
            </Link>
          </div>
        </div>
      </header>

      {/* Filters toolbar */}
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card sm:flex-row sm:items-center sm:gap-4">
        <div className="min-w-0 flex-1">
          <Input
            prefix={<Search size={18} strokeWidth={2} aria-hidden />}
            placeholder="Search by customer name…"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-52">
          <Select
            placeholder="All statuses"
            className="w-full"
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            allowClear
          >
            {statusOptions.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
        </div>
        {hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="shrink-0 text-sm font-semibold text-brand hover:text-brand-dark"
          >
            Clear filters
          </button>
        ) : null}
        <p className="text-xs text-slate-500 sm:ml-auto sm:text-right">
          Showing{" "}
          <span className="font-semibold text-slate-800">
            {filteredOrders.length}
          </span>{" "}
          of {ordersCount} orders
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Revenue"
          value={
            <>
              {formatAED(totalAmount)}{" "}
              <span className="text-lg font-bold text-slate-500">AED</span>
            </>
          }
          hint={hasFilters ? "Filtered order total" : "All loaded orders"}
          icon={Wallet}
          accent="violet"
        />
        <KpiCard
          label="Orders"
          value={filteredOrders.length}
          hint={`${ordersCount} total in system`}
          icon={ShoppingBag}
          accent="sky"
        />
        <KpiCard
          label="Customers"
          value={usersCount}
          hint="Registered profiles"
          icon={UsersIcon}
          accent="emerald"
        />
        <KpiCard
          label="Avg. order"
          value={
            <>
              {formatAED(avgOrderValue)}{" "}
              <span className="text-lg font-bold text-slate-500">AED</span>
            </>
          }
          hint={
            unpaidCount > 0
              ? `${unpaidCount} unpaid · filtered set`
              : "Based on filtered orders"
          }
          icon={unpaidCount > 0 ? AlertCircle : TrendingUp}
          accent="amber"
        />
      </div>

      {/* Revenue chart */}
      <Panel
        title="Revenue trend"
        subtitle="Daily totals · last 30 days · respects filters above"
      >
        {chartData.length === 0 ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 text-center">
            <Package className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
            <p className="text-sm font-medium text-slate-600">
              No revenue data for this period
            </p>
            <p className="max-w-xs text-xs text-slate-500">
              Try clearing filters or wait for new orders in the last 30 days.
            </p>
          </div>
        ) : (
          <div className="h-[300px] w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}`}
                />
                <RechartsTooltip
                  formatter={(value) => [`${formatAED(value)} AED`, "Revenue"]}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 12px 40px rgba(15,23,42,0.12)",
                    fontSize: 13,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#5b21b6"
                  strokeWidth={2.5}
                  fill="url(#revenueFill)"
                  activeDot={{ r: 5, fill: "#5b21b6", strokeWidth: 0 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>

      {/* Orders + status */}
      <div className="grid gap-6 xl:grid-cols-5">
        <Panel
          className="xl:col-span-3"
          title="Recent orders"
          subtitle="Newest first · filtered list"
          action={
            <Link
              to="/orders"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:text-brand-dark"
            >
              View all
              <ArrowRight size={14} aria-hidden />
            </Link>
          }
        >
          <div className="overview-table -mx-5 -mb-5 overflow-hidden">
            <Table
              rowKey="_id"
              dataSource={filteredOrders}
              columns={[
                {
                  title: "Ref",
                  dataIndex: "ref",
                  key: "ref",
                  render: (ref) => (
                    <span className="font-mono text-xs font-semibold text-slate-700">
                      #{ref ?? "—"}
                    </span>
                  ),
                },
                {
                  title: "Customer",
                  dataIndex: "userId",
                  key: "userId",
                  render: (userx) => (
                    <span className="font-medium text-slate-900">
                      {getName(userx)}
                    </span>
                  ),
                },
                {
                  title: "Total",
                  dataIndex: "totalPrice",
                  key: "totalPrice",
                  render: (data) => (
                    <span className="tabular-nums font-semibold text-slate-900">
                      {formatAED(data)} <span className="text-slate-400">AED</span>
                    </span>
                  ),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  key: "status",
                  render: (status) => <StatusBadge status={status} />,
                },
                {
                  title: "Payment",
                  dataIndex: "balance",
                  key: "balance",
                  render: (data) =>
                    Number(data) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                        <AlertCircle size={14} aria-hidden />
                        Unpaid
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-emerald-700">
                        Paid
                      </span>
                    ),
                },
                {
                  title: "Date",
                  dataIndex: "createdAt",
                  key: "createdAt",
                  render: (date) => (
                    <span className="text-xs text-slate-600">
                      {convertToReadableDate(date)}
                    </span>
                  ),
                },
              ]}
              pagination={{ pageSize: 8, showSizeChanger: false }}
            />
          </div>
        </Panel>

        <Panel
          className="xl:col-span-2"
          title="Order status"
          subtitle="Distribution of filtered orders"
        >
          {statusData.length === 0 ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center text-sm text-slate-500">
              No orders match this filter.
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="mx-auto h-[200px] w-full max-w-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={80}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {statusData.map((_e, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        borderRadius: 10,
                        border: "1px solid #e2e8f0",
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="space-y-2.5">
                {statusData.map((item, index) => {
                  const pct = filteredOrders.length
                    ? Math.round((item.value / filteredOrders.length) * 100)
                    : 0;
                  return (
                    <li
                      key={item.name}
                      className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor:
                              CHART_COLORS[index % CHART_COLORS.length],
                          }}
                        />
                        <span className="truncate text-sm font-medium text-slate-800">
                          {item.name}
                        </span>
                      </div>
                      <div className="shrink-0 text-right">
                        <span className="text-sm font-bold tabular-nums text-slate-900">
                          {item.value}
                        </span>
                        <span className="ml-1.5 text-xs text-slate-500">
                          {pct}%
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default Dashboard;
